import React from 'react';

/**
 * Product definitions used by the subscription checkbox grid.
 * Each entry maps a Circle ADOR field key to a user-facing label
 * and short description.
 * @type {Array<{key: string, label: string, description: string}>}
 */
const PRODUCTS = [
  { key: 'Sub_uCreatePrint', label: 'uCreate Print', description: 'Variable data print design' },
  { key: 'Sub_uProduce', label: 'uProduce', description: 'Production & workflow automation' },
  { key: 'Sub_uStore', label: 'uStore', description: 'Web-to-print storefront' },
  { key: 'Sub_Circle', label: 'Circle', description: 'Omnichannel campaign management' },
  { key: 'Sub_uPlan', label: 'uPlan', description: 'Campaign planning & design' },
  { key: 'Sub_XMPieVideoService', label: 'XMPie Video Service', description: 'Personalized video creation' },
  { key: 'Sub_XMPieEmailService', label: 'XMPie Email Service', description: 'Email campaign delivery' },
  { key: 'Sub_uImage', label: 'uImage', description: 'Dynamic image generation' },
];

/**
 * ProductCheckboxes — reusable subscription checkbox grid.
 *
 * Renders a "Subscribe to all" toggle and an individual checkbox for
 * each XMPie product.  Toggling "all" sets every product; toggling a
 * single product auto-syncs the "all" state.
 *
 * @param {Object}   props
 * @param {Object}   props.subscriptions        - Map of ADOR key → boolean.
 * @param {Function} props.onChange              - Called with the updated subscriptions map.
 * @param {boolean}  props.subscribeAll          - Whether "subscribe all" is checked.
 * @param {Function} props.onSubscribeAllChange  - Called with the new subscribeAll boolean.
 */
export default function ProductCheckboxes({ subscriptions, onChange, subscribeAll, onSubscribeAllChange }) {
  const handleToggleAll = (e) => {
    const checked = e.target.checked;
    onSubscribeAllChange(checked);
    const updated = {};
    PRODUCTS.forEach(p => { updated[p.key] = checked; });
    onChange(updated);
  };

  const handleToggle = (key) => {
    const updated = { ...subscriptions, [key]: !subscriptions[key] };
    onChange(updated);

    const allChecked = PRODUCTS.every(p => updated[p.key]);
    if (allChecked !== subscribeAll) {
      onSubscribeAllChange(allChecked);
    }
  };

  return (
    <div className="product-checkboxes">
      <div className="subscribe-all-row">
        <label className="checkbox-label checkbox-label--all">
          <input
            type="checkbox"
            checked={subscribeAll}
            onChange={handleToggleAll}
          />
          <span className="checkbox-custom" />
          <span className="checkbox-text">Subscribe to all products</span>
        </label>
      </div>
      <div className="product-grid">
        {PRODUCTS.map(product => (
          <label key={product.key} className="checkbox-label checkbox-label--product">
            <input
              type="checkbox"
              checked={!!subscriptions[product.key]}
              onChange={() => handleToggle(product.key)}
            />
            <span className="checkbox-custom" />
            <span className="checkbox-text">
              <strong>{product.label}</strong>
              <small>{product.description}</small>
            </span>
          </label>
        ))}
      </div>
    </div>
  );
}

export { PRODUCTS };
